import React, { useState, useMemo} from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import IconButton from '@mui/material/IconButton';
import {AppCTX} from '../data/AppContext';
import PinDropIcon from '@mui/icons-material/PinDrop';
import ButtonGroup from '@mui/material/ButtonGroup';
import Stack from '@mui/material/Stack';
import CallIcon from '@mui/icons-material/Call';
import ClientDetails from './ClientDetails';
import ListItemButton from '@mui/material/ListItemButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddBoxIcon from '@mui/icons-material/AddBox';
import Slide from '@mui/material/Slide';
import dayjs from 'dayjs';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`appointments-tabpanel-${index}`}
      aria-labelledby={`appointments-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function AppointmentsList() {
  const { map, setStatus, appointments, deleteProspect, user} = React.useContext(AppCTX);
  const [showDetails, setShowDetails] = useState(false);
  const [updateProspect, setUpdateProspect] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const containerRef = React.useRef(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleClickOpen = (prospect) => {
    setUpdateProspect(prospect);
    map.panTo(prospect.coords);
    map.setZoom(20);
    setShowDetails(true);
  };

  const copyLocation = (prospect) => {
    navigator.clipboard.writeText(prospect.address);
    setStatus({notificationType:'success', message: 'address copied to clipboard'});
  };

  const renderAppointmentCard = (prospect) => {
    const primaryContact = prospect.owners[0];
    const secondaryContact = prospect.owners.length > 1 ? prospect.owners[1] : false;
    const appointmentTime = dayjs(prospect.appointmentStart).format('MMM D, h:mm A');
    
    // Only render if user is the closer or setter
    if (user.data.name === prospect.closer || user.data.name === prospect.setter) {
      return (
        <div id={`${primaryContact.name}-${primaryContact.number}`} key={`${primaryContact.name}-${primaryContact.number}`}>
          <Stack direction="row" spacing={2}>
            <ListItem alignItems="flex-start">
              <ListItemButton 
                sx={{
                  width:'80%',
                }} 
                onClick={()=>handleClickOpen(prospect)}
              >
                <ListItemText
                  primary={secondaryContact ? `${primaryContact.name} & ${secondaryContact.name}` : `${primaryContact.name}`}
                  secondary={
                    <>
                      <Typography
                        sx={{ display: 'block' }}
                        component="span"
                        variant="body2"
                        
                      >
                        {`Appointment: ${appointmentTime}`}
                      </Typography>
                      <Typography
                        sx={{ display: 'block' }}
                        component="span"
                        variant="body2"
                        
                      >
                        {`${prospect.notes[prospect.notes.length-1]}`}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
              <ButtonGroup orientation="vertical" size="large" aria-label="large button group">
                <IconButton onClick={()=>copyLocation(prospect)} aria-label="navigate">
                  <PinDropIcon/>
                </IconButton>
                <IconButton href={`tel:${primaryContact.number}`} aria-label="call">
                  <CallIcon/>
                </IconButton>
              </ButtonGroup>
              <ButtonGroup orientation="vertical" size="large" aria-label="large button group">
                <IconButton aria-label="add">
                  <AddBoxIcon/>
                </IconButton>
                <IconButton onClick={()=>deleteProspect(prospect.address)} aria-label="delete">
                  <DeleteIcon/>
                </IconButton>
              </ButtonGroup>
            </ListItem>
          </Stack>
        </div>
      );
    }
    return null;
  };

  const { upcomingAppointments, pastAppointments } = useMemo(() => {
    const now = dayjs();
    const upcoming = [];
    const past = [];

    // Sort appointments into upcoming and past
    appointments.forEach(appointment => {
      const appointmentTime = dayjs(appointment.appointmentStart);
      if (appointmentTime.isBefore(now)) {
        past.push(appointment);
      } else {
        upcoming.push(appointment);
      }
    });

    // Sort both arrays by time
    upcoming.sort((a, b) => 
      dayjs(a.appointmentStart).diff(dayjs(b.appointmentStart))
    );
    past.sort((a, b) => 
      dayjs(b.appointmentStart).diff(dayjs(a.appointmentStart))
    );

    return { upcomingAppointments: upcoming, pastAppointments: past };
  }, [appointments]);

  if(appointments) {
    return (
      <>
        <Slide in container={containerRef.current}>
          <List sx={{ width: '90%', marginRight:'10px', marginLeft:'10px' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': { color: '#197278' },
                  '& .Mui-selected': { color: '#197278 !important' },
                  '& .MuiTabs-indicator': { backgroundColor: '#197278' }
                }}
              >
                <Tab 
                  label={`Upcoming (${upcomingAppointments.length})`} 
                  id="appointments-tab-0"
                  aria-controls="appointments-tabpanel-0"
                />
                <Tab 
                  label={`Past (${pastAppointments.length})`} 
                  id="appointments-tab-1"
                  aria-controls="appointments-tabpanel-1"
                />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {upcomingAppointments.length > 0 ? (
                upcomingAppointments.map(prospect => renderAppointmentCard(prospect))
              ) : (
                <Typography variant="subtitle1" sx={{ color: '#197278' }}>
                  No upcoming appointments scheduled.
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {pastAppointments.length > 0 ? (
                pastAppointments.map(prospect => renderAppointmentCard(prospect))
              ) : (
                <Typography variant="subtitle1" sx={{ color: '#197278' }}>
                  No past appointments.
                </Typography>
              )}
            </TabPanel>

            {upcomingAppointments.length === 0 && pastAppointments.length === 0 && (
              <Typography variant="subtitle1" sx={{ mt: 2, color: '#197278' }}>
                To add an appointment first add a prospect. Then tap on the '+' icon to open the Appointment scheduler. Set the appointment by selecting a time and date then press save.
              </Typography>
            )}
          </List>
        </Slide>
        <ClientDetails
          selectedProspect={updateProspect}
          open={showDetails}
          setShowDetails={setShowDetails}
          setSelectedProspect={setUpdateProspect}
        />
      </>
    );
  }
}

export default AppointmentsList;